import React, { useEffect, useRef, useState } from 'react';
import { mapCacheService, type AddressData } from '@/services/maps/MapCacheService';

interface MapDisplayProps {
  lat?: number;
  lng?: number;
  initialLocation?: { lat: number; lng: number };
  onLocationChange?: (lat: number, lng: number) => void;
  height?: string;
  interactive?: boolean;
  useCache?: boolean;
  controls?: boolean;
}

const MapDisplay: React.FC<MapDisplayProps> = ({ 
  lat: propLat, 
  lng: propLng, 
  initialLocation,
  onLocationChange,
  height = '100%',
  interactive = true,
  useCache = true,
  controls = true
}) => {
  // Usar initialLocation se fornecido, caso contrário usar lat/lng
  const lat = initialLocation ? initialLocation.lat : propLat || 0;
  const lng = initialLocation ? initialLocation.lng : propLng || 0;
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [cachedAddress, setCachedAddress] = useState<AddressData | null>(null);
  const [isCached, setIsCached] = useState<boolean>(false);

  // Verificar se existe cache para a localização atual
  useEffect(() => {
    if (useCache) {
      const address = mapCacheService.findCachedAddress(lat, lng);
      if (address) {
        setCachedAddress(address);
        setIsCached(true);
        console.log('Usando dados de endereço em cache:', address);
      } else {
        setCachedAddress(null);
        setIsCached(false);
      }
    }
  }, [lat, lng, useCache]);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Importar Leaflet dinamicamente para evitar problemas de SSR
    const initializeMap = async () => {
      try {
        const L = await import('leaflet');
        
        // Verificar se o mapa já foi inicializado
        if (mapRef.current) {
          // Atualizar a posição do marcador se as coordenadas mudaram
          if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
            mapRef.current.setView([lat, lng], mapRef.current.getZoom());
          }
          return;
        }
        
        // Fix Leaflet icon issue
        const DefaultIcon = L.icon({
          iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });
        
        L.Marker.prototype.options.icon = DefaultIcon;
        
        // Inicializar o mapa
        if (mapContainerRef.current) {
          mapRef.current = L.map(mapContainerRef.current, {
            zoomControl: interactive,
            dragging: interactive,
            scrollWheelZoom: interactive
          }).setView([lat, lng], 15);

          // Camadas base
          const osmStandard = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors'
          });
          const osmHot = L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors, Tiles style by Humanitarian OpenStreetMap Team hosted by OpenStreetMap France'
          });

          // Adicionar camada padrão
          osmStandard.addTo(mapRef.current);

          // Controles opcionais: layers e escala
          if (controls) {
            const baseLayers: Record<string, any> = {
              'OSM Standard': osmStandard,
              'OSM Humanitarian': osmHot
            };
            L.control.layers(baseLayers).addTo(mapRef.current);
            L.control.scale({ metric: true, imperial: false }).addTo(mapRef.current);
          }
        
          // Adicionar marcador na posição atual
          markerRef.current = L.marker([lat, lng], {
            draggable: interactive
          }).addTo(mapRef.current);
          
          // Se tiver endereço em cache, adicionar popup com informações
          if (isCached && cachedAddress) {
            const addressText = formatAddressFromCache(cachedAddress);
            markerRef.current.bindPopup(`<b>Endereço em cache:</b><br>${addressText}`).openPopup();
          }
        
          // Adicionar evento de arrastar o marcador se for interativo
          if (interactive && onLocationChange) {
            markerRef.current.on('dragend', (e: any) => {
              const marker = e.target;
              const position = marker.getLatLng();
              onLocationChange(position.lat, position.lng);
            });
            
            // Adicionar evento de clique no mapa
            mapRef.current.on('click', (e: any) => {
              const { lat, lng } = e.latlng;
              markerRef.current.setLatLng([lat, lng]);
              // Quando a localização muda, verificar se há cache para a nova posição
              if (useCache) {
                const address = mapCacheService.findCachedAddress(lat, lng);
                if (address) {
                  setCachedAddress(address);
                  setIsCached(true);
                  const addressText = formatAddressFromCache(address);
                  markerRef.current.bindPopup(`<b>Endereço em cache:</b><br>${addressText}`).openPopup();
                } else {
                  setCachedAddress(null);
                  setIsCached(false);
                  markerRef.current.closePopup();
                }
              }
              
              onLocationChange(lat, lng);
            });
          }

          // Controle custom "Localizar-me"
          if (controls) {
            const LocateControl = L.Control.extend({
              options: { position: 'topleft' as const },
              onAdd: function () {
                const container = L.DomUtil.create('div', 'leaflet-bar');
                const link = L.DomUtil.create('a', '', container);
                link.href = '#';
                link.title = 'Localizar-me';
                link.innerHTML = '◎';
                link.style.textAlign = 'center';
                link.style.width = '28px';
                link.style.display = 'inline-block';
                link.style.lineHeight = '26px';
                link.style.fontSize = '16px';

                L.DomEvent.on(link, 'click', (e: any) => {
                  L.DomEvent.stopPropagation(e);
                  L.DomEvent.preventDefault(e);
                  if (!navigator.geolocation) return;
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      const { latitude, longitude } = pos.coords;
                      mapRef.current.setView([latitude, longitude], 16);
                      if (markerRef.current) {
                        markerRef.current.setLatLng([latitude, longitude]);
                      }
                      if (useCache) {
                        const address = mapCacheService.findCachedAddress(latitude, longitude);
                        if (address) {
                          setCachedAddress(address);
                          setIsCached(true);
                          const addressText = formatAddressFromCache(address);
                          markerRef.current.bindPopup(`<b>Endereço em cache:</b><br>${addressText}`).openPopup();
                        } else {
                          setCachedAddress(null);
                          setIsCached(false);
                          if (markerRef.current) markerRef.current.closePopup();
                        }
                      }
                      if (onLocationChange) onLocationChange(latitude, longitude);
                    },
                    () => {
                      // silently ignore errors
                    },
                    { enableHighAccuracy: true, timeout: 15000 }
                  );
                });

                return container;
              }
            });

            const locateControl = new (LocateControl as any)();
            mapRef.current.addControl(locateControl);
          }
        }
      } catch (error) {
        console.error('Erro ao inicializar o mapa:', error);
      }
    };
    
    initializeMap();
    
    // Limpar o mapa quando o componente for desmontado
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [lat, lng, interactive, onLocationChange, controls, useCache, isCached, cachedAddress]);

  // Função para formatar o endereço do cache para exibição
  const formatAddressFromCache = (address: AddressData): string => {
    const parts = [];
    
    if (address.road) {
      const roadPart = address.house_number 
        ? `${address.road}, ${address.house_number}` 
        : address.road;
      parts.push(roadPart);
    }
    
    if (address.suburb) parts.push(address.suburb);
    
    const cityState = [];
    if (address.city) cityState.push(address.city);
    if (address.state) cityState.push(address.state);
    if (cityState.length > 0) parts.push(cityState.join(' - '));
    
    if (address.postcode) parts.push(address.postcode);
    
    return parts.join('<br>');
  };

  return (
    <div 
      ref={mapContainerRef} 
      style={{ height, width: '100%' }}
      className={`leaflet-container ${isCached ? 'cached-location' : ''} touch-manipulation`}
    />
  );
};

export default MapDisplay;
